import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import getText from '../../../shared/i18n/labels'

// COMPONENTS
import { SelectableElement } from './SelectableElement'

/**
 * DropdownMultipleSelect è un componente React riutilizzabile che consente di selezionare
 * una o più opzioni da un menu a discesa. Le opzioni selezionate vengono visualizzate
 * come chip, offrendo un'interfaccia chiara e intuitiva per la selezione multipla.
 *
 * @param {array} options - Un array di oggetti con proprietà id e description.
 * @param {function} onSelectionChange - Una funzione di callback che verrà chiamata
 * quando l'utente clicca sul pulsante Applica. La funzione riceverà le opzioni
 * selezionate come argomento.
 * @param {array} value - Un array di oggetti con proprietà id e description.
 * Il componente utilizzerà questo array per preselezionare le opzioni.
 *
 */
export function DropdownMultipleSelect({
  options = [{ id: null, description: '' }],
  onSelectionChange = (selectedOptions) => {},
  value = []
}) {
  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const [selectedOptions, setSelectedOptions] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)

  useEffect(() => {
    setSelectedOptions(value)
  }, [value])

  /**
   * Funzione di callback per gestire il click su un'opzione del menu a discesa.
   * Se l'opzione è già selezionata, la rimuove dalla lista delle opzioni
   * selezionate, altrimenti la aggiunge.
   *
   * @param {object} option - l'opzione selezionata
   */
  const handleOptionClick = (option) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((o) => o !== option))
    } else {
      setSelectedOptions([...selectedOptions, option])
    }
  }

  /**
   * Funzione di callback per gestire il click sul pulsante Applica.
   * Richiama la funzione di callback onSelectionChange con le opzioni selezionate
   * e imposta showDropdown a false per nascondere il menu a discesa.
   */
  const handleConfirmClick = () => {
    onSelectionChange(selectedOptions)
    setShowDropdown(false)
  }

  /**
   * Funzione di callback per gestire il click sul pulsante Reset.
   * Richiama la funzione di callback onSelectionChange con un array vuoto
   * e imposta showDropdown a false per nascondere il menu a discesa.
   */
  const handleResetClick = () => {
    onSelectionChange([])
    setShowDropdown(false)
  }

  return (
    <>
      <button
        title={
          selectedOptions?.length > 0
            ? selectedOptions.map((o) => o.description).join(', ')
            : undefined
        }
        className={`dropdown-toggle ${selectedOptions?.length > 0 ? '' : 'text-placeholder'}`}
        onClick={() => setShowDropdown(!showDropdown)}>
        <span className={`ps-0 ms-0`}>
          {selectedOptions?.length > 0
            ? `${selectedOptions.length} ${selectedOptions.length === 1 ? labels.SELECTED_ELEMENT : labels.SELECTED_ELEMENTS}`
            : labels.SELECT_OPTIONS}
        </span>
      </button>
      {selectedOptions?.length > 0 && (
        <button className="btn-clear-dropdown" onClick={() => handleResetClick()}>
          <i className="star-icon-cancel star-icon" />
        </button>
      )}
      {showDropdown && (
        <div className="dropdownMultipleSelect">
          <div className="dropdownMultipleSelect-content">
            {options.map((option, index) => (
              <SelectableElement
                key={`dropdown-multiple-select-${index}`}
                value={option}
                defaultChecked={selectedOptions.includes(option)}
                onChange={() => handleOptionClick(option)}
                label={option.description}
              />
            ))}
          </div>
          <div className="dropdownMultipleSelect-footer justify-content-between">
            <button className="btn btn-outline-primary mb-1 ms-1 mt-2" onClick={handleResetClick}>
              {labels.RESET}
            </button>
            <button className="btn btn-primary mb-1 me-1 mt-2" onClick={handleConfirmClick}>
              {labels.APPLY}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
